/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

import pluginFactory from 'taoTests/runner/plugin';
import defaultPluginConfig from './pluginConfig.js';
import { getTestSessionUserDataService } from '../../../session/testSessionUserDataService.js';
import MenuPanel from './MenuPanel.svelte';
import settingsKeys from '../../settings/settingsKeys.js';
import queueFactory from '../../integration/eventsForwarder/queue.js';
import { defaultsDeepNoArrayMerge } from '../../../util/common.js';
import { mount, unmount } from 'svelte';
import {
    injectA11yContentOverrideStyle,
    removeA11yContentOverrideStyles
} from 'taoQtiNuiItem/runner/item/stylesheets/a11yCustomStyles.js';
import { stateSnapshot } from '../../../../svelteUtils/snapshot.svelte.js';
import { createProps } from '../../../../svelteUtils/props.svelte.js';

/**
 * This plugin provides the accessibility panel content
 */
export default pluginFactory({
    name: 'a11yMenuPanel',

    install() {
        const testRunner = this.getTestRunner();
        const testConfig = testRunner.getConfig();

        const pluginConfig = defaultsDeepNoArrayMerge(
            {},
            testRunner.getPluginConfig(this.getName()),
            defaultPluginConfig
        );
        this.setConfig(pluginConfig);

        //const sessionStatus = getTestSessionStatusStore(testConfig.serviceCallId);
        const testSessionUserDataService = getTestSessionUserDataService(testConfig.serviceCallId);

        this.settingsStore = testSessionUserDataService.getSettingsStore();

        //ui-log
        queueFactory({
            id: `${this.getName()}.${testRunner.getConfig().serviceCallId}`,
            bufferSize: 10,
            flush(events) {
                return testRunner.getProxy().callTestAction('ui-log', { events });
            }
        }).then(eventsQueue => {
            this.eventsQueue = eventsQueue;
        });
        this.eventsQueueEnqueue = (domEventType, metadata) => {
            if (pluginConfig.eventLog?.enabled) {
                this.eventsQueue.enqueue({
                    domEventType,
                    itemIdentifier: testRunner.getTestContext().itemIdentifier,
                    metadata: {
                        timeStamp: Date.now(),
                        component: `plugin-${this.getName()}`,
                        ...metadata
                    }
                });
            }
        };
        this.panelOpen = false;
    },

    init() {
        const testRunner = this.getTestRunner();
        const pluginConfig = this.getConfig();

        testRunner.on('unloaditem itemModalFeedback', () => {
            this.eventsQueue.flush();
        });

        testRunner.on('proctor-reset', async () => {
            if (this.eventsQueue) {
                await this.eventsQueue.clear();
            }

            this.settingsStore.setSetting(settingsKeys.a11yMenuPanel, {});
            Object.keys(pluginConfig).forEach(key => {
                this.settingsStore.setSetting(key, null);
            });

            this.panelOpen = false;
        });

        //add to settings to pass to item-runner
        const a11ySetting = this.settingsStore.getSetting(settingsKeys.a11yMenuPanel) || {};
        a11ySetting.convertPxToRem = pluginConfig.convertPxToRem;
        this.settingsStore.setSetting(settingsKeys.a11yMenuPanel, a11ySetting);
    },

    /**
     * Renders the a11y menu panel contents
     */
    render() {
        const testRunner = this.getTestRunner();
        const areaBroker = testRunner.getAreaBroker();
        const pluginConfig = this.getConfig();

        // retrieve previously saved config from settingsStore
        const initialSettingsState = Object.keys(pluginConfig).reduce((acc, key) => {
            acc[key] =
                this.settingsStore.getSetting(settingsKeys.a11yMenuPanel)?.[key] || this.settingsStore.getSetting(key);
            return acc;
        }, {});

        // register changes from settings components
        const onChange = ({ key: toolKey, state: toolState }) => {
            // store user's latest changes for a future rendering
            this.settingsStore.setSetting(toolKey, {
                toolState: stateSnapshot(toolState)
            });

            // enqueue a ui-log event
            this.eventsQueueEnqueue('change', {
                toolKey,
                toolState
            });
        };
        // track Details components opening/closing
        const onToggle = ({ key, collapsed }) => {
            const groupSetting = this.settingsStore.getSetting(settingsKeys.a11yMenuPanel) || {};
            groupSetting[key] = {
                collapsed
            };
            this.settingsStore.setSetting(settingsKeys.a11yMenuPanel, groupSetting);
        };
        // Changes from Panel interior
        const onOpen = () => {
            this.panelOpen = true;

            this.eventsQueueEnqueue('custom', {
                type: 'panel-open'
            });
        };
        const onClose = () => {
            this.panelOpen = false;

            this.eventsQueueEnqueue('custom', {
                type: 'panel-close'
            });
        };

        this.menuPanelProps = createProps({
            open: this.panelOpen,
            areaBroker,
            pluginConfig,
            initialSettingsState,
            onChange,
            onToggle,
            onOpen,
            onClose
        });

        //render the plugin component
        this.menuPanel = mount(MenuPanel, {
            target: areaBroker.getA11yMenuPanelArea(),
            props: this.menuPanelProps
        });

        injectA11yContentOverrideStyle(areaBroker.getContainer(), '.qti-item');

        // Changes from runner lifecycle
        testRunner
            .on('renderitem.a11yMenuPanel.first', () => {
                testRunner.off('renderitem.a11yMenuPanel.first');
                if (pluginConfig.openOnStart) {
                    this.show();
                }
            })
            .on('toolbaraction.a11yMenuPanel', key => {
                if (key === 'a11yMenuPanel') {
                    if (this.panelOpen) {
                        this.hide();
                    } else {
                        this.show();
                    }
                }
            })
            .on('timeout pause finish error', () => {
                this.hide();
            });
    },

    show() {
        this.menuPanelProps.open = true;
    },

    hide() {
        this.menuPanelProps.open = false;
    },

    /**
     * Destroys the plugin
     */
    destroy() {
        if (this.menuPanel) {
            unmount(this.menuPanel);
        }

        removeA11yContentOverrideStyles(this.getTestRunner().getAreaBroker().getContainer());

        this.eventsQueue?.flush();

        if (typeof this.settingsStoreUnsubscribe === 'function') {
            this.settingsStoreUnsubscribe();
        }
        if (this.unsubscribeFromStatusChanges) {
            this.unsubscribeFromStatusChanges();
        }
    }
});
